import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Group } from '../api/client';
import { ancestrosDe, enArbol, rutaDe } from '../grupos/arbol';
import { Palomita } from './Iconos';

/** A partir de aquí buscar sale más a cuenta que recorrer la lista con la vista. */
const UMBRAL_DE_BUSQUEDA = 8;

/**
 * Quién ve un documento.
 *
 * Se usa en los dos sitios donde se decide —al crear y al corregir la
 * audiencia— y por eso es un componente y no dos listas parecidas: son la misma
 * decisión, y cuando se ven distintas la gente cree que hacen cosas distintas.
 *
 * Las casillas sueltas de antes tenían un problema concreto: con el texto al
 * lado de un cuadrito de 13 px, el objetivo de clic era la palabra y poco más, y
 * saber cuántos grupos llevabas elegidos obligaba a contarlos. Aquí cada grupo
 * es una tarjeta entera pulsable, el estado se ve por color y por marca —no sólo
 * por color— y la cuenta está escrita.
 */
export function SelectorDeGrupos({
  grupos,
  elegidos,
  onCambio,
  deshabilitado = false,
  idEtiqueta,
  avisoVacio,
  incluyeRama = false,
}: {
  grupos: Group[];
  elegidos: string[];
  onCambio: (ids: string[]) => void;
  deshabilitado?: boolean;
  /** Para que el `aria-describedby` de quien lo usa pueda apuntar a la cuenta. */
  idEtiqueta?: string;
  /**
   * Qué decir cuando no hay ninguno elegido, y con cuánta alarma.
   *
   * No es lo mismo en los dos sitios donde se usa esto, y confundirlos sería
   * mentir en uno de ellos: un documento **no puede** quedarse sin grupos —el
   * servidor lo rechaza, nadie lo vería— mientras que una persona sí puede, y
   * simplemente no verá documentos hasta que se le asigne alguno. Lo primero es
   * un error que hay que impedir; lo segundo, una consecuencia que hay que
   * advertir. Un recuadro rojo en el segundo caso enseña a ignorar los rojos.
   */
  avisoVacio?: { texto: string; grave?: boolean };
  /**
   * Si elegir un grupo alcanza también a sus subgrupos (ADR-0027). Así es para
   * la audiencia de un documento; no para la membresía de una persona, que está
   * en el grupo que se elige y en ningún otro.
   */
  incluyeRama?: boolean;
}) {
  const { t } = useTranslation();
  const [filtro, setFiltro] = useState('');

  // En orden de árbol, y se busca también por la ruta: «crédito» encuentra a
  // sus subgrupos aunque su nombre no lo diga.
  const visibles = useMemo(() => {
    const texto = filtro.trim().toLowerCase();
    return enArbol(grupos)
      .map(({ grupo }) => grupo)
      .filter((g) => !texto || rutaDe(grupos, g.id).toLowerCase().includes(texto));
  }, [grupos, filtro]);

  /** El elegido de más arriba que ya lo alcanza, si lo hay. */
  const incluidoPor = (grupo: Group) =>
    incluyeRama && !elegidos.includes(grupo.id)
      ? ancestrosDe(grupos, grupo.id).find((arriba) => elegidos.includes(arriba.id))
      : undefined;

  const alternar = (id: string) =>
    onCambio(elegidos.includes(id) ? elegidos.filter((x) => x !== id) : [...elegidos, id]);

  return (
    <div className="grupos-selector">
      <div className="grupos-selector__barra">
        <span className="grupos-selector__cuenta" id={idEtiqueta}>
          {t('groups.chosenCount', { chosen: elegidos.length, total: grupos.length })}
        </span>
        <div className="grupos-selector__acciones">
          <button
            type="button"
            className="boton boton--discreto"
            disabled={deshabilitado || elegidos.length === grupos.length}
            onClick={() => onCambio(grupos.map((g) => g.id))}
          >
            {t('groups.chooseAll')}
          </button>
          <button
            type="button"
            className="boton boton--discreto"
            disabled={deshabilitado || elegidos.length === 0}
            onClick={() => onCambio([])}
          >
            {t('groups.chooseNone')}
          </button>
        </div>
      </div>

      {grupos.length > UMBRAL_DE_BUSQUEDA && (
        <input
          className="grupos-selector__filtro"
          type="search"
          value={filtro}
          placeholder={t('groups.filterPlaceholder')}
          onChange={(e) => setFiltro(e.target.value)}
          aria-label={t('groups.filterPlaceholder')}
        />
      )}

      {visibles.length === 0 ? (
        <p className="vacio">{t('groups.noneMatch')}</p>
      ) : (
        <div className="grupos-selector__rejilla">
          {visibles.map((grupo) => {
            const padre = grupo.parentId ? rutaDe(grupos, grupo.parentId) : '';
            const porArriba = incluidoPor(grupo);
            return (
            <label
              key={grupo.id}
              className={`casilla-ficha${elegidos.includes(grupo.id) ? ' casilla-ficha--marcada' : ''}${
                porArriba ? ' casilla-ficha--incluida' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={elegidos.includes(grupo.id)}
                disabled={deshabilitado}
                onChange={() => alternar(grupo.id)}
              />
              {/* La marca acompaña al color en vez de sustituirlo: el estado no
                  puede depender de distinguir dos verdes. */}
              <span className="casilla-ficha__marca" aria-hidden="true">
                <Palomita />
              </span>
              <span className="casilla-ficha__nombre">
                {grupo.name}
                {/* De dónde cuelga, y si ya lo alcanza un grupo elegido arriba:
                    elegirlo otra vez no cambia quién lo ve. */}
                {(padre || porArriba) && (
                  <span className="casilla-ficha__ruta">
                    {porArriba ? t('groups.includedBy', { group: porArriba.name }) : padre}
                  </span>
                )}
              </span>
            </label>
            );
          })}
        </div>
      )}

      {elegidos.length === 0 &&
        avisoVacio &&
        (avisoVacio.grave ? (
          <p className="grupos-selector__aviso" role="alert">
            {avisoVacio.texto}
          </p>
        ) : (
          <p className="campo__ayuda">{avisoVacio.texto}</p>
        ))}
    </div>
  );
}
